import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import WorkplacesService from '../../../../services/WorkplacesService';
import splitArray from '../../../../utils/splitArray';
import { MappedWorkplace, WorkplaceFromApiObject } from '../../types';

interface UseLoadWorkplacesI { setCurrentPage: Dispatch<SetStateAction<number>> }

export default function useLoadWorkplaces({ setCurrentPage }: UseLoadWorkplacesI) {
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [doesListApiHasError, setDoesListApiHasError] = useState(false);

  const [workplaces, setWorkplaces] = useState<MappedWorkplace[]>([]);
  const [filteredWorkplaces, setFilteredWorkplaces] = useState<MappedWorkplace[]>([]);
  const [workplacesSplitted, setWorkplacesSplitted] = useState<MappedWorkplace[][]>([]);
  const [fullWorkplacesInfo, setFullWorkplacesInfo] = useState<WorkplaceFromApiObject[]>([]);

  const { apiCall } = useApiCall();
  const { userProfile, userCode, companyCode } = useGetGeneralAppData();

  const loadWorkplaces = useCallback(async () => {
    await apiCall({
      onStartLoad: () => setIsLoadLoading(true),
      onEndLoad: () => setIsLoadLoading(false),
      apiToCall: WorkplacesService.listWorkplaces,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
      },
      actionAfterResponse: (apiResponse) => {
        const didApiFetchedSuccessfully = apiResponse.codigo;
        if (!didApiFetchedSuccessfully) {
          const isEmptyList = apiResponse.msg === 'Não existem Endereços cadastrados na empresa.';

          if (!isEmptyList) {
            toast.error(`Não foi possível carregar a lista de locais de trabalho (${apiResponse.msg})`);
            setDoesListApiHasError(true);
          }
          setFullWorkplacesInfo([]);
          setWorkplaces([]);
          setWorkplacesSplitted([]);
          setFilteredWorkplaces([]);
          setCurrentPage(0);
          return;
        }

        const workplacesListFromApi: WorkplaceFromApiObject[] = apiResponse.dadosEndereco;
        setFullWorkplacesInfo(workplacesListFromApi);
        const mappedWorkplacesList = workplacesListFromApi.map((wp) => ({
          id: wp.codEnd,
          active: !!wp.endAtivo,
          code: wp.codEndNoclienteAlfaN,
          streetName: wp.logradouro,
          city: wp.cidade,
          uf: wp.UF,
          name: wp.denominacaoLT,
        }));
        setWorkplaces(mappedWorkplacesList);

        const splitedArray = splitArray(mappedWorkplacesList);
        setWorkplacesSplitted(splitedArray);
        setFilteredWorkplaces(splitedArray[0]);
        setCurrentPage(0);
        setDoesListApiHasError(false);
      },
      catchMessage: 'Não foi possível carregar a lista de locais de trabalho',
    });
  }, [apiCall, companyCode, setCurrentPage, userCode, userProfile]);

  useEffect(() => {
    if (!companyCode) {
      return;
    }
    loadWorkplaces();
  }, [loadWorkplaces, companyCode]);

  return {
    isLoadLoading,
    workplaces,
    doesListApiHasError,
    filteredWorkplaces,
    setFilteredWorkplaces,
    loadWorkplaces,
    workplacesSplitted,
    setWorkplacesSplitted,
    fullWorkplacesInfo,
  };
}
